import { Fragment, memo, useMemo, useState } from 'react';
import { SignatoriesVM, SignatureActVM } from 'civil-status/view-models/step-form';
import { FormControl, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { FormikProps } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { noop } from 'shell/utils/noop';
import { RadioGroup } from '@mui/material';
import { Radio } from '@mui/material';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { HttpResponse } from 'shell/models/http-response';
import { AttachmentsData } from 'shell/models/act-wizard';
import FormDigitalSignature from './form-digital-signature';
import FormAutographsSignature from './form-autographs-signature';
import { TYPE_DOCUMENT_PROCESSO_VERBALE } from 'civil-status/constants/type-document-processo-verbale';

interface FormSignatureActProps {
  formContext: FormikProps<SignatureActVM>;
  idEvento?: number;
  onYesPreview: () => void;
  onClickVerifyDocument: (document?: TypeFile) => void | ResponsePromise<string>;
}

const FormSignatureActComponent = ({
  formContext,
  idEvento,
  onYesPreview,
  onClickVerifyDocument,
}: FormSignatureActProps): React.ReactElement => {
  const [openModal, setOpenModal] = useState<boolean>(false);

  const values: SignatureActVM = useMemo(() => {
    return formContext.values || {};
  }, [formContext.values]);

  const isAutografa: boolean = useMemo(() => {
    return values.signatories?.find((item) => item.isAutografa)?.isAutografa || false;
  }, [values]);

  const onChange = useEventCallback(
    (
      itemChange: SignatoriesVM,
      key: keyof SignatoriesVM,
      value: SignatoriesVM[keyof SignatoriesVM]
    ) => {
      const signatoriesCopy = (values.signatories || []).slice();

      const signatoriesNew = signatoriesCopy.map((item) => {
        if (item.idSubject === itemChange.idSubject) {
          return {
            ...item,
            [key]: value,
          };
        }

        return { ...item };
      });

      formContext.setFieldValue('signatories', signatoriesNew);
    }
  );

  const onChanges = useEventCallback((itemChange: SignatoriesVM, valuesToSave: SignatoriesVM) => {
    const signatoriesCopy = (values.signatories || []).slice();

    const signatoriesNew = signatoriesCopy.map((item) => {
      if (item.idSubject === itemChange.idSubject) {
        return {
          ...item,
          ...valuesToSave,
        };
      }

      return { ...item };
    });

    formContext.setFieldValue('signatories', signatoriesNew);
  });

  const sendData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-dichiarante-elettronica/firmadichiarante/invio-email`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickSend = useEventCallback((item: SignatoriesVM) => {
    const data = {
      idSoggettoSide: item.idSubject ? [item.idSubject] : undefined,
      idEventoSide: idEvento?.toString() || undefined,
      firmaDichiaranteOnlineInvioEmailRequest: {
        emailDichiarante: item.email || undefined,
        idEvento:
          values.idAnsc && values.idAnsc.split('-') ? values.idAnsc.split('-')[1] : undefined,
        idAnsc: values.idAnsc || undefined,
        firmatario: {
          cognome: item.surname || undefined,
          nome: item.name || undefined,
        },
      },
    };

    (
      sendData({ data }) as unknown as ResponsePromise<HttpResponse<{ idPresaVisione?: string }>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const idPresaVisione = response?.data.payload.idPresaVisione || '';

      onChanges(item, {
        isSend: true,
        idPresaVisione,
      });
    });
  });

  const cancelData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-dichiarante-elettronica/firmadichiarante/annulla-link`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickCancel = useEventCallback((item: SignatoriesVM) => {
    const data = {
      idSoggettoSide: item.idSubject ? [item.idSubject] : undefined,
      idEventoSide: idEvento?.toString() || undefined,
      firmaDichiaranteOnlineAnnullaLinkRequest: {
        idPresaVisione: item.idPresaVisione || undefined,
      },
    };

    (cancelData({ data }) as unknown as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      onChanges(item, {
        isSend: false,
        idPresaVisione: undefined,
      });
    });
  });

  const insertData = useHttpClient(`${__ACT_API_URL__}/allegato-atti/inserimento`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};

      // inserire un record sulla tabella allegato
      const data = {
        idEvento,
        nomeFile: file.name || undefined,
        oggettoAllegato: (result as string)?.split(',')[1],
        tipoAllegato: TYPE_DOCUMENT_PROCESSO_VERBALE, // fixed
      };

      (
        insertData({
          data,
        }) as ResponsePromise<HttpResponse<AttachmentsData>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const document: TypeFile = {
          ...file,
          raw: result as string,
          name: file.name || '',
          idAllegato: response?.data.payload.key?.idAllegato,
          codStato: response?.data.payload.codStato,
        };

        formContext.setFieldValue('document', document);
      });
    });

    reader.readAsDataURL(file);
  });

  const deleteData = useHttpClient(``, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const onTrashPress = useEventCallback(() => {
    if (!Boolean(values.document?.idAllegato)) {
      return;
    }

    (
      deleteData({
        url: `${__ACT_API_URL__}/allegato-atti/cancellazione/${values.document?.idAllegato}`,
      }) as unknown as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      formContext.setFieldValue('document', undefined);
    });
  });

  const uploadDocumentData = useHttpClient(`${__ACT_API_URL__}/firme/firmadichiarante/upload`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickSendDocument = useEventCallback(() => {
    const data = {
      idEventoSide: idEvento?.toString() || undefined,
      idSoggettoSide: (values.signatories || [])
        ?.filter((item) => item.isAutografa)
        ?.map((item) => item.idSubject),
      idAllegato: values.document?.idAllegato,
      firmaDichiaranteRequest: {
        firmaDichiaranteInput: {
          contenuto: values.document?.raw
            ? values.document?.raw?.split('base64,')[1] || values.document?.raw || undefined
            : undefined,
          nomefile: values.document?.name || undefined,
          tipoAllegato: TYPE_DOCUMENT_PROCESSO_VERBALE, // fixed
          tipoFile: '1', // fixed
          stato: '1', // fixed
          idEvento:
            values.idAnsc && values.idAnsc.split('-') ? values.idAnsc.split('-')[1] : undefined,
          listaFirmatari: (
            values.signatories?.filter((itemSignatorie) => itemSignatorie.isAutografa) || []
          ).map((item) => ({
            nome: item.name || undefined,
            cognome: item.surname || undefined,
            codiceFiscale: item.fiscalCode || undefined,
            sesso: item.gender || undefined,
            dataNascita: item.dateBorn ? item.dateBorn.toFormat('yyyy-MM-dd') : undefined,
          })),
        },
      },
    };

    (
      uploadDocumentData({ data }) as unknown as ResponsePromise<
        HttpResponse<{ firmaDichiaranteOutput: { idAllegato?: string; statoAllegato?: string } }>
      >
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      formContext.setFieldValue('document', {
        ...values.document,
        idAnscAllegato: response?.data?.payload?.firmaDichiaranteOutput?.idAllegato,
        codStato: response?.data?.payload?.firmaDichiaranteOutput?.statoAllegato,
      });

      // chiusura modale
      setOpenModal(false);
    });
  });

  const verifyData = useHttpClient(
    `${__ACT_API_URL__}/firme/firma-dichiarante-elettronica/firmadichiarante/pooling-statoatto`,
    {
      options: {
        method: 'POST',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onClickVerify = useEventCallback((item?: SignatoriesVM) => {
    if (!item?.idPresaVisione) {
      return;
    }
    const data = {
      idSoggettoSide: item?.idSubject ? [item?.idSubject] : undefined,
      idEventoSide: idEvento?.toString() || undefined,
      firmaDichiaranteOnlinePoolingStatoAttoRequest: {
        idPresaVisione: item?.idPresaVisione || undefined,
      },
    };

    (verifyData({ data }) as unknown as ResponsePromise<HttpResponse<{ idState?: number }>>).then(
      (response) => {
        toast.success('Operazione eseguita con successo');

        onChange(item, 'isVerify', response?.data.payload.idState === 1);
      }
    ) as ResponsePromise<string>;
  });

  const onVerifyDocument = useEventCallback(() => {
    onClickVerifyDocument((formContext.values as SignatureActVM)?.document)?.then((res) => {
      const codStato = res as string | undefined;

      formContext.setFieldValue('document', {
        ...(formContext.values as SignatureActVM)?.document,
        codStato,
      });
    });
  });

  return (
    <Fragment>
      {(values.signatories || []).map((item, index) => {
        return (
          <Grid
            item
            container
            spacing={2}
            xs={12}
            key={`${item.name || ''}-${item.surname || ''}-${index}`}
          >
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Nome firmatario"
                label="Nome firmatario"
                id="nameSurname"
                name="nameSurname"
                fullWidth
                disabled
                type="text"
                variant="outlined"
                value={`${item?.name || ''} ${item.surname || ''}`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data nascita"
                aria-label="Data nascita"
                value={item?.dateBorn || null}
                disabled
                onChange={noop}
                renderInput={(params) => (
                  <PmiTextField {...params} id="birthDate" fullWidth helperText="" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label=""
                  id="isAutografa"
                  value={item?.isAutografa || 'false'}
                  onChange={() => {
                    onChanges(item, { isAutografa: !item?.isAutografa, email: '' });
                    // pulire il DB dall'allegato
                    onTrashPress();
                  }}
                >
                  <FormControlLabel
                    disabled={
                      item.isSend ||
                      item.isVerify ||
                      (item.isAutografa && Boolean(values.document?.idAnscAllegato))
                    }
                    value="true"
                    control={<Radio />}
                    label="Firma autografa *"
                  />
                  <FormControlLabel
                    disabled={
                      item.isSend ||
                      item.isVerify ||
                      (item.isAutografa && Boolean(values.document?.idAnscAllegato))
                    }
                    value="false"
                    control={<Radio />}
                    label="Firma digitale *"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {item.isAutografa ? undefined : (
              <FormDigitalSignature
                item={item}
                onChange={onChange}
                onClickCancel={onClickCancel}
                onClickVerify={onClickVerify}
                onClickSend={onClickSend}
              />
            )}
          </Grid>
        );
      })}
      {isAutografa ? (
        <FormAutographsSignature
          values={values}
          handleAddFile={handleAddFile}
          onYesPreview={onYesPreview}
          onTrashPress={onTrashPress}
          setOpenModal={setOpenModal}
          onVerifyDocument={onVerifyDocument}
        />
      ) : undefined}
      {openModal ? (
        <DeleteAlertModal
          title="Attenzione!"
          message="Una volta inviato il file selezionato, non è più possibile modificare l'evento. Vuoi procedere?"
          open={openModal}
          onClose={() => setOpenModal(false)}
          onYes={onClickSendDocument}
          onNo={() => setOpenModal(false)}
          yesError
        />
      ) : undefined}
    </Fragment>
  );
};

FormSignatureActComponent.displayName = 'Form Signature Act component';

const FormSignatureAct = memo(FormSignatureActComponent);

export default FormSignatureAct;
